import { Fragment, memo } from "react";

import { Tab } from "@headlessui/react";

import { Text } from "@react/components/Text";
import { ThemeMode } from "@root/tablescape/state";
import { tw } from "classnames";

const styles = {
  container: tw("overflow-y-auto", "w-full", "pb-3", "pt-6"),
  tabs: tw(
    "w-max",
    "min-w-full",
    "md:w-full",
    "md:h-16",
    "flex",
    "flex-row",
    "flex-nowrap",
    "border-solid",
    "border-r-0",
    "border-l-0",
    "border-t-0",
    "border-b",
    "border-char-200",
    "px-6",
    "pt-1",
    "md:px-0",
    "list-none",
    "box-border"
  ),
  tab: tw(
    "box-border",
    "min-w-24",
    "max-w-72",
    "min-h-11",
    "md:min-h-15",
    "flex-1",
    "mx-1",
    "md:mx-0",
    "py-1",
    "px-2",
    "text-center",
    "cursor-pointer",
    "flex",
    "flex-col",
    "justify-center",
    "items-center",
    "md:whitespace-nowrap",
    "md:py-4",
    "md:px-4"
  ),
  selectedTab: tw(
    "border-solid",
    "border-r-0",
    "border-l-0",
    "border-t-0",
    "border-b-2",
    "border-special",
    "-mb-px"
  ),
  selectedLabel: tw("text-special", "leading-tight"),
  unselectedLabel: tw("text-primary", "leading-tight"),
};

type Props = {
  defaultIndex?: number;
  onTabClick: (selectedIndex: number) => void;
  tabs: string[];
  theme?: ThemeMode;
};

const TabsBase = ({
  defaultIndex = 0,
  onTabClick,
  tabs,
  theme = ThemeMode.LIGHT,
}: Props) => {
  return (
    <Tab.Group
      as="div"
      className={styles.container}
      data-theme={theme}
      defaultIndex={defaultIndex}
      onChange={onTabClick}
    >
      <Tab.List className={styles.tabs}>
        {tabs.map((tab) => (
          <Tab as={Fragment} key={`tab-label-${tab}`}>
            {({ selected }) => (
              <div
                className={tw({
                  [styles.tab]: true,
                  [styles.selectedTab]: selected,
                })}
              >
                <Text
                  as="button"
                  className={tw({
                    [styles.selectedLabel]: selected,
                    [styles.unselectedLabel]: !selected,
                  })}
                >
                  {tab}
                </Text>
              </div>
            )}
          </Tab>
        ))}
      </Tab.List>
    </Tab.Group>
  );
};

export const Tabs = memo(TabsBase);
